import { getSourceMaterialType, MaterialTabType } from "@/types/MaterialTabs";
import { useAppSettings } from "@api/appSettings";
import i18n from "@i18n/i18n";
import { GridColDef } from "@mui/x-data-grid-premium";
import { getClientColumnConfig, getClientConfigKey } from "@utils/clientConfigUtils";

type ExtendedGridColDef = {
  hidden?: boolean;
  export?: boolean;
} & GridColDef;

const getI18nText = (headerNameKey: string, descriptionKey: string) => ({
  headerName: i18n.t(`${headerNameKey}`),
  description: i18n.t(`${descriptionKey}`),
  placeholder: i18n.t(`${headerNameKey}`),
});

const getColumnByField = (col: any): ExtendedGridColDef | undefined => {
  if (!col || !col.field || !col.headerNameKey || !col.descriptionKey) {
    console.warn(`Invalid column definition: ${JSON.stringify(col)}`);
    return undefined;
  }

  const i18nText = getI18nText(col.headerNameKey, col.descriptionKey);

  const baseColumn: Partial<ExtendedGridColDef> = {
    field: col.field,
    ...i18nText,
    flex: col.flex || 1,
    minWidth: col.minWidth,
    editable: col.editable ?? false,
    hidden: col.hidden,
  };

  if (col.sourceProperty) {
    return {
      ...baseColumn,
      type: col.type || "string",
      valueGetter: (_value: any, row: any) => {
        if (col.field === "materialType") {
          return getSourceMaterialType(row.sourceData) ?? "";
        }
        try {
          return col.sourceProperty.split(".").reduce((acc: any, key: string) => acc?.[key], row.sourceData);
        } catch {
          return "";
        }
      },
    } as ExtendedGridColDef;
  }

  return {
    ...baseColumn,
    type: col.type || "string",
  } as ExtendedGridColDef;
};

export const useMaterialColumns = () => {
  const appSettings = useAppSettings();
  const clientKey = getClientConfigKey(appSettings.data);
  const config = getClientColumnConfig(clientKey);
  const allColumns = config?.columns || [];

  const filterColumns = (fields: string[]): ExtendedGridColDef[] =>
    allColumns.map((col: any) => getColumnByField(col)).filter((col1) => fields.includes(col1!.field)) as ExtendedGridColDef[];

  const getDefaultColumns = (): GridColDef[] => {
    const defaultFields = ["name", "category", "a1A2A3", "unit", "description", "source"];
    return filterColumns(defaultFields);
  };

  const getTabColumns = (tab: MaterialTabType): ExtendedGridColDef[] => {
    switch (tab) {
      case "material":
        return filterColumns([
          "externalId",
          "materialType",
          "name",
          "description",
          "reference_period",
          "unit",
          "attribute1",
          "attribute2",
          "attribute3",
          "location",
          "a1A2A3",
          "a4",
          "a5",
          "b1",
          "b2",
          "b3",
          "b4",
          "b5",
          "c1",
          "c2",
          "c3",
          "c4",
          "d",
          "biogenic",
          "density",
          "massPerUnit",
          "wastage",
          "comment",
          "mapping_variable",
          "reference_multiplier",
          "source",
          "carbonFactorId",
        ]);
      case "product":
        return filterColumns([
          "externalId",
          "materialType",
          "name",
          "system",
          "subsystem",
          "description",
          "reference_period",
          "unit",
          "location",
          "a1A2A3",
          "a4",
          "a5",
          "b1",
          "b2",
          "b3",
          "b4",
          "b5",
          "c1",
          "c2",
          "c3",
          "c4",
          "d",
          "biogenic",
          "massPerUnit",
          "wastage",
          "comment",
          "mapping_variable",
          "reference_multiplier",
          "source",
          "carbonFactorId",
        ]);
      case "assembly":
        return filterColumns([
          "externalId",
          "materialType",
          "name",
          "system",
          "subsystem",
          "description",
          "reference_period",
          "unit",
          "attribute1",
          "attribute2",
          "attribute3",
          "location",
          "a1A2A3",
          "a4",
          "a5",
          "b1",
          "b2",
          "b3",
          "b4",
          "b5",
          "c1",
          "c2",
          "c3",
          "c4",
          "d",
          "biogenic",
          "massPerUnit",
          "wastage",
          "comment",
          "mapping_variable",
          "source",
          "carbonFactorId",
        ]);
      default:
        return getDefaultColumns();
    }
  };

  return { getDefaultColumns, getTabColumns };
};
